import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cs from 'classnames';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PushPinIcon from '@mui/icons-material/PushPin';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { switchSharedWithMeModal, updateSharedFeed } from '../../../../store/folders/foldersSlice';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../../enums/ACCESS_ENUM';
import { ReactComponent as TimelineIcon } from '../../../../assets/icons/timeline.svg';
import { ReactComponent as ComposerIcon } from '../../../../assets/icons/composer.svg';
import { ReactComponent as MovieIcon } from '../../../../assets/icons/movieCollection.svg';
import { DateFormatter, bytesToGB, bytesToMB, findSearchValue, secondsToHours } from '../../../../helpers';
import { FOLDER_CONTENT_FILTER, PATHS, VIDEOFEED_STATUS, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../../enums';
import useOnClickOutside from '../../../../helpers/useOnClickOutside';
import StorageIcon from '../../../../assets/icons/storage.svg';
import { VIDEOFEED_WITH_SIZE } from '../sharedVideosList';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import styles from './SharedVideofeed.module.scss';
import { MoreIcon } from '../../../../assets/icons/threedots';
import DownloadButton from '../../../modules/DownloadButton';
import DownloadDataModal from '../../../modules/VideoFileList/DownloadDataModal';
import ShareButton from '../../../modules/ShareButton';
import { convertSeconds } from '../../../Timeline/utils';
import { Tooltip } from '@mui/material';
import { pinContent, unpinContent } from '../../../../api/pinnedContentApi';
import { Portal } from '../../../../helpers/bodyPortal';
import { SortOptions } from '../../../../enums/SORT';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../../../modules/DateSortHeader';
import FileTypeThumbnail from '../../../modules/FileTypeThumbnail';
import { useOverflowAwareActionMenu } from '../../../../helpers/useOverflowAwareActionMenu';
import ReplaceThumbnailModal from '../../../modules/ReplaceThumbnailModal';
import { removeVideofeedThumbnail, uploadVideofeedThumbnail } from '../../../../api/videofeeds';
import ThumbnailButton from '../../../modules/ThumbnailButton';

type CustomEventHandler = React.MouseEvent<HTMLDivElement> | React.MouseEvent<SVGSVGElement>;

type TSharedVideofeed = {
  feed: VIDEOFEED_WITH_SIZE;
  i: number;
  folderId?: string;
  forceMovieIcon?: boolean;
  referenceTimelines?: VIDEOFEED_WITH_SIZE[];
  feedIndexById?: Record<string, number>;
  isReferenceChild?: boolean;
  embedded?: boolean;
  forceBlueTimelineIcon?: boolean;
  pinContextType?: string;
  pinContextParentId?: string | number | null;
  onPinUpdated?: () => void;
  dateSortOption?: SortOptions | null;
};

const SharedVideofeed: React.FC<TSharedVideofeed> = ({
  feed,
  i,
  folderId,
  referenceTimelines,
  feedIndexById,
  isReferenceChild = false,
  embedded = false,
  forceBlueTimelineIcon = false,
  pinContextType,
  pinContextParentId,
  onPinUpdated,
  dateSortOption,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const page = findSearchValue('page');
  const limit = findSearchValue('limit');
  const sort = findSearchValue('sort');
  const search = findSearchValue('search');
  const folderTrail = findSearchValue('folderTrail');
  const contentFilter = findSearchValue('contentFilter');
  const { currentUser, myOrganization } = useAppSelector((state) => state.user);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(feed.thumbnail_url || '');
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ x: number; y: number } | null>(null);
  const { actionMenuRef, triggerRef, menuPosition } = useOverflowAwareActionMenu({
    showMenu: showDotsMenu,
    anchorPosition: menuAnchorPosition,
  });
  const previewUrl = thumbnailUrl || feed?.videos?.[0]?.s3_preview_url || '';
  const organizationHaveDownloadHours = Number(myOrganization?.download_hours_limit || 0) > 0;
  const canShowMyComposerDownload = feed.type !== VIDEOFEED_TYPE.MY_COMPOSER || organizationHaveDownloadHours;
  const canDownload = canShowMyComposerDownload && (feed.highest_user_access?.allow_downloads || feed.created_by.id == currentUser?.id);
  const isPinned = Boolean((feed as any)?.is_pinned);
  const canReplaceThumbnail =
    feed.type === VIDEOFEED_TYPE.COMPOSER &&
    !isReferenceChild &&
    !embedded &&
    feed.status === VIDEOFEED_STATUS.DONE &&
    (feed.created_by?.id === currentUser?.id ||
      feed.highest_user_access?.access?.title === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE);
  const hasTopicRows = (feed?.topics?.length || 0) > 0;
  const isTopLevelTimeline =
    feed.type === VIDEOFEED_TYPE.TIMELINE &&
    (feed.parent_videofeed_id === null || feed.parent_videofeed_id === undefined);
  const isLegacyTopLevelTimelineCollection = isTopLevelTimeline && hasTopicRows;
  const isCollectionVisual = Boolean(feed.is_collection_visual);
  const displayReferenceTimelines =
    (referenceTimelines?.length || 0) > 0
      ? referenceTimelines!
      : !isReferenceChild && !embedded && isLegacyTopLevelTimelineCollection
        ? [feed]
        : [];
  const hasReferencesSection = displayReferenceTimelines.length > 0;
  const isCollectionRow =
    !isReferenceChild && !embedded && (isCollectionVisual || isLegacyTopLevelTimelineCollection || hasReferencesSection);
  const isCollectionTimelineRow = isReferenceChild && embedded;
  const rawSizeBytes = Number(
    feed.size || feed.videos.reduce((a, b) => Number(a) + Number(b.size), 0),
  );
  const displaySizeBytes = isCollectionTimelineRow ? 0 : rawSizeBytes;
  const uploadHoursTooltip = feed?.duration
    ? isCollectionTimelineRow
      ? `Upload Hours: REF ${secondsToHours(feed.duration)}`
      : `Upload Hours: ${convertSeconds(feed.duration, false, true)}`
    : '';
  const sourceTitle = isCollectionRow ? feed.collection_name || feed.title : feed.title;
  const [referencesExpanded, setReferencesExpanded] = useState(hasReferencesSection);
  const showExpandedReferences = hasReferencesSection && referencesExpanded && !embedded;
  const shellCollectionTarget =
    !embedded &&
    isCollectionRow &&
    (feed?.topics?.length || 0) === 0 &&
    displayReferenceTimelines.length > 0
      ? displayReferenceTimelines[0]
      : null;

  useEffect(() => {
    setReferencesExpanded((prev) =>
      hasReferencesSection ? prev || hasReferencesSection : false,
    );
  }, [hasReferencesSection]);

  useEffect(() => {
    setThumbnailUrl(feed.thumbnail_url || '');
  }, [feed.thumbnail_url]);

  const redirect = (e: CustomEventHandler, videofeed: VIDEOFEED_WITH_SIZE, feedIndex: number) => {
    if (e.target !== e.currentTarget) return;
    const resolvedFeed = shellCollectionTarget && videofeed.id === feed.id ? shellCollectionTarget : videofeed;
    const resolvedFeedIndex =
      resolvedFeed.id === videofeed.id
        ? feedIndex
        : feedIndexById?.[String(resolvedFeed.id)] ?? feedIndex;
    switch (resolvedFeed.type) {
      case VIDEOFEED_TYPE.COMPOSER:
        history.push(
          `${PATHS.COMPOSER_PAGE.path}?feedId=${resolvedFeed.id}&folderId=${folderId}&shared=true
          &fileside=true&feed=${resolvedFeedIndex}&clipId=${resolvedFeed.videos[0].id}&clip=0&parentPage=${PATHS.SHARED_WITH_ME.label}
          &pageType=${VIDEOPAGE_TYPE.COMPOSER}&page=${page}&limit=${limit}&sort=${sort}&search=${search}${
            folderTrail ? `&folderTrail=${folderTrail}` : ''
          }${
            contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
              ? `&contentFilter=${contentFilter}`
              : ''
          }`,
        );
        break;
      case VIDEOFEED_TYPE.MY_COMPOSER:
        history.push(
          `${PATHS.COMPOSER_PAGE.path}?feedId=${resolvedFeed.id}&shared=true&fileside=true&feed=${resolvedFeedIndex}&clipId=${resolvedFeed.videos[0].id}
          &clip=0&parentPage=${PATHS.SHARED_WITH_ME.label}&pageType=${VIDEOFEED_TYPE.MY_COMPOSER}&page=${page}&limit=${limit}&sort=${sort}&search=${search}${
            folderTrail ? `&folderTrail=${folderTrail}` : ''
          }${
            contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
              ? `&contentFilter=${contentFilter}`
              : ''
          }`,
        );
        break;
      default:
        history.push(
          `${PATHS.TIMELINE.path}?feedId=${resolvedFeed.id}&folderId=${folderId}&shared=true
          &fileside=true&feed=${resolvedFeedIndex}&clipId=${resolvedFeed.videos[0].id}&clip=0&parentPage=${PATHS.SHARED_WITH_ME.label}
          &pageType=${VIDEOPAGE_TYPE.TIMELINE}&page=${page}&limit=${limit}&sort=${sort}&search=${search}${
            folderTrail ? `&folderTrail=${folderTrail}` : ''
          }${
            contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
              ? `&contentFilter=${contentFilter}`
              : ''
          }`,
        );
    }
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
      setMenuAnchorPosition(null);
      return;
    }
    if (e.type === 'contextmenu') {
      setMenuAnchorPosition({ x: e.clientX, y: e.clientY });
    } else {
      setMenuAnchorPosition(null);
    }
    setShowDotsMenu(true);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
    setMenuAnchorPosition(null);
  });

  const switchDownloadDataModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const onDownload = (e: any) => {
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowDownloadModal(true);
  };

  const onShareClick = (shareScope?: 'single' | 'collection') => {
    setShowDotsMenu(false);
    const isCollectionShareTarget =
      !isReferenceChild &&
      !embedded &&
      Boolean((feed as any)?.is_collection_root);
    dispatch(
      updateSharedFeed({
        ...(feed as any),
        share_scope:
          shareScope ||
          (feed as any)?.highest_user_access?.share_scope ||
          (feed as any)?.share_scope ||
          (isCollectionShareTarget ? 'collection' : 'single'),
      } as any),
    );
    dispatch(switchSharedWithMeModal(true));
    setShowDotsMenu(false);
  };

  const onPinToggle = async () => {
    if (!pinContextType) return;
    try {
      if (isPinned) {
        await unpinContent({
          targetType: 'videofeed',
          targetId: feed.id,
          contextType: pinContextType,
          contextParentId: pinContextParentId,
        });
      } else {
        await pinContent({
          targetType: 'videofeed',
          targetId: feed.id,
          contextType: pinContextType,
          contextParentId: pinContextParentId,
        });
      }
      onPinUpdated?.();
      notification.showMessage(isPinned ? 'Unpinned' : 'Pinned');
      setShowDotsMenu(false);
    } catch {
      notification.showMessage('Failed to update pin');
    }
  };

  const onReplaceThumbnail = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowThumbnailModal(true);
  };

  return (
    <div
      className={cs(
        styles.rowWrapper,
        embedded && styles.embeddedRowWrapper,
        hasReferencesSection && !embedded && styles.groupParentRowWrapper,
        isPinned && !embedded && styles.pinnedCollection,
      )}
    >
      <div
        className={cs(
          styles.container,
          showDotsMenu && styles.selected,
          embedded && styles.embeddedContainer,
          isReferenceChild && styles.referenceChild,
          showExpandedReferences && styles.parentExpanded,
        )}
        onClick={(e) => {
          redirect(e, feed, i);
        }}
        onContextMenu={switchDotsMenu}
        style={{ position: 'relative' }}
      >
        <div className={styles.leftSide} onClick={(e) => redirect(e, feed, i)}>
          <FileTypeThumbnail
            className={isCollectionTimelineRow ? undefined : styles.fileTypeThumbnail}
            iconOnly={isCollectionTimelineRow}
            icon={
              feed.type === VIDEOFEED_TYPE.COMPOSER ? (
                <ComposerIcon className={styles.contentTypeIcon} style={{ pointerEvents: 'none' }} />
              ) : forceBlueTimelineIcon || isReferenceChild ? (
                <TimelineIcon
                  className={cs(styles.contentTypeIcon, isCollectionTimelineRow && styles.collectionTimelineIcon)}
                  style={{ pointerEvents: 'none', color: '#39b3ea' }}
                />
              ) : (
                <MovieIcon className={styles.contentTypeIcon} style={{ pointerEvents: 'none' }} />
              )
            }
          >
            <img
              onClick={(e) => redirect(e, feed, i)}
              src={
                previewUrl || 'https://ik.imagekit.io/x7qrmqxln99/https://songdewnetwork.com/sgmedia/assets/images/default-video-image.png'
              }
              alt={'video feed'}
              className={styles.thumbnail}
            />
          </FileTypeThumbnail>
          <div onClick={(e) => redirect(e, feed, i)} className={styles.name}>
            {sourceTitle}
          </div>
        </div>
        <div className={styles.team} onClick={(e) => redirect(e, feed, i)}>
          {feed.created_by.username}
        </div>
        <div className={styles.access} onClick={(e) => redirect(e, feed, i)}>
          {feed?.highest_user_access ? ACCESS_TYPE[feed.highest_user_access.access.title as ACCESS_TYPE_ENUM] : ''}
        </div>
        <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(feed, dateSortOption), true)}`}>
          <div className={styles.date} onClick={(e) => redirect(e, feed, i)}>
            {DateFormatter(getDateColumnValue(feed, dateSortOption), true)}
          </div>
        </Tooltip>

        <Tooltip title={uploadHoursTooltip}>
          <div className={styles.size} onClick={(e) => redirect(e, feed, i)}>
            <img src={StorageIcon} alt="size" />
            {bytesToGB(displaySizeBytes, 2) > 1
              ? `${bytesToGB(displaySizeBytes, 2)} GB`
              : `${bytesToMB(displaySizeBytes, 0)} MB`}
          </div>
        </Tooltip>

        <div className={styles.dotsMenuBtn}>
          {hasReferencesSection && !embedded && (
            <div
              className={styles.expandIcon}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setReferencesExpanded((prev) => !prev);
              }}
            >
              {referencesExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </div>
          )}
          <div ref={triggerRef} className={styles.dotIcon} onClick={switchDotsMenu}>
            <MoreIcon />
          </div>
        </div>

        <Portal>
          <div
            ref={actionMenuRef}
            className={styles.actionMenu}
            style={{
              position: 'fixed',
              display: showDotsMenu ? 'block' : 'none',
              top: `${menuPosition.y}px`,
              left: `${menuPosition.x}px`,
              right: 'auto',
            }}
          >
            <div
              className={cs(
                styles.menuOption,
                (feed?.highest_user_access && feed?.highest_user_access.access.title === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE) ||
                  feed?.highest_user_access.access.title === ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE
                  ? ''
                  : styles.disabled,
              )}
              onClick={() => onShareClick()}
            >
              <ShareButton /> Share
            </div>
            {canReplaceThumbnail && (
              <div className={styles.menuOption} onClick={onReplaceThumbnail}>
                <ThumbnailButton /> Replace Thumbnail Image
              </div>
            )}
            {canDownload && feed.status === VIDEOFEED_STATUS.DONE ? (
              <div className={styles.menuOption} onClick={onDownload}>
                <DownloadButton /> Download
              </div>
            ) : null}
            {pinContextType && !isReferenceChild && !embedded && (
              <div className={styles.menuOption} onClick={onPinToggle}>
                <span style={{ display: 'inline-flex', width: 35, height: 35, padding: 4, borderRadius: 'var(--ui-border-radius)', background: 'var(--action-menu-icon-bg)', alignItems: 'center', justifyContent: 'center', marginRight: 12, boxSizing: 'border-box' }}>
                  <PushPinIcon sx={{ fontSize: 20, color: '#fff' }} />
                </span>
                {isPinned ? 'Unpin' : 'Pin'}
              </div>
            )}
          </div>
        </Portal>
        <ReplaceThumbnailModal
          open={showThumbnailModal}
          setOpen={setShowThumbnailModal}
          onUpload={(data) => uploadVideofeedThumbnail(feed.id, data)}
          onUploaded={(updatedFeed) => setThumbnailUrl(updatedFeed.thumbnail_url || '')}
          onRestore={thumbnailUrl ? () => removeVideofeedThumbnail(feed.id) : undefined}
          preview={{ type: 'file', title: feed.title, imageUrl: previewUrl }}
        />
        <DownloadDataModal switchModal={switchDownloadDataModal} isOpen={showDownloadModal} feedId={feed.id} />
      </div>

      {showExpandedReferences && (
        <div className={styles.referencesList}>
          {displayReferenceTimelines.map((timeline) => (
            <SharedVideofeed
              key={`timeline-${timeline.id}`}
              feed={timeline}
              i={feedIndexById?.[String(timeline.id)] || i}
              folderId={folderId}
              isReferenceChild
              embedded
              forceBlueTimelineIcon
              referenceTimelines={[]}
              feedIndexById={feedIndexById}
              pinContextType={pinContextType}
              pinContextParentId={pinContextParentId}
              onPinUpdated={onPinUpdated}
              dateSortOption={dateSortOption}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SharedVideofeed;
